import { EmptyState } from "@/components/common/EmptyState/EmptyState"
import { Alert } from "@/components/ui/alert"
import { Skeleton } from "@/components/ui/skeleton"
import { Typography } from "@/components/ui/typography"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { CircleAlert } from "lucide-react"
import type { ReactNode } from "react"

/** Title and description with an action to their right, like a settings page heading. */
export function UpgradeHeading({
  title,
  description,
  action,
}: {
  title: string
  description: string
  action?: ReactNode
}) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div className="flex min-w-0 flex-col gap-1">
        <Typography as="h3" role="heading" aria-level={1}>
          {title}
        </Typography>
        <Typography as="muted">{description}</Typography>
      </div>
      {action}
    </div>
  )
}

/** Placeholder plan cards while the plans load. */
export function UpgradeSkeleton() {
  const { translate } = useTranslation()
  return (
    <div
      role="status"
      aria-busy="true"
      aria-label={translate("UPGRADE/LOADING")}
      className="grid gap-6 md:grid-cols-3"
    >
      {[0, 1, 2].map((index) => (
        <Skeleton key={index} className="h-80 rounded-xl" />
      ))}
    </div>
  )
}

/** A failure with its way out, in the error look of SettingsLoadError. */
export function UpgradeError({
  description,
  action,
}: {
  description: string
  action: ReactNode
}) {
  const { translate } = useTranslation()
  return (
    <Alert variant="destructive">
      <CircleAlert className="size-4" />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-col gap-1">
          <Typography as="small">{translate("UPGRADE/ERROR_TITLE")}</Typography>
          <Typography as="muted">{description}</Typography>
        </div>
        {action}
      </div>
    </Alert>
  )
}

/** A notice or result that takes the whole page, in the app's EmptyState look. */
export function UpgradeState({
  icon,
  title,
  description,
  actions,
  role,
  dataTestId,
}: {
  /** A lucide icon or a Spinner, size-6. */
  icon: ReactNode
  title: string
  description?: string
  actions?: ReactNode
  role?: "status" | "alert"
  dataTestId?: string
}) {
  return (
    <EmptyState
      bordered={false}
      role={role}
      dataTestId={dataTestId}
      illustration={
        <div className="flex size-14 items-center justify-center rounded-2xl border bg-card text-muted-foreground">
          {icon}
        </div>
      }
      title={title}
      description={description}
      actions={actions}
    />
  )
}
